import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaTimes, FaPlus, FaImage, FaTrash, FaArrowRight } from 'react-icons/fa';
import { Cloudinary } from '@cloudinary/url-gen';
import SEO from '../../components/SEO';
import './events.css';
import { isAdmin } from '../admin/isAdmin';
import api from '../../services/api';

const Events = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const language = i18n.language;
  const isArabic = language === 'ar';
  const isRTL = i18n.language === 'ar';

  // Configuration Cloudinary
  const cld = new Cloudinary({
    cloud: {
      cloudName: 'dozvy3xru'
    }
  });

  // URL des vidéos
  const videoEvent1 = cld.video('igzqtk5kgn3onynlcim7.mp4');
  const videoEvent2 = cld.video('vibkck3da5yc2pcyhrnv.mp4');
  const videoEvent3 = cld.video('rff0wd0ptwlvuwpcfjfj.mp4');
  const videoEvent4 = cld.video('lrh2a5uyhmhkbnlszbvc.mp4');

  const [activeFilter, setActiveFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('upcoming'); // 'upcoming' ou 'past'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentDate] = useState(new Date());
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [showAddEventForm, setShowAddEventForm] = useState(false);
  const [galleryImages, setGalleryImages] = useState(['']);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    category: 'workshop',
    image: '',
    gallery: [],
    outcomes: ['', '', ''],
    health: false,
    culture: false,
    sports: false,
    professional: false,
    internal: false,
    external: false,
    status: 'upcoming',
    type: 'internal'
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fonction pour obtenir l'URL correcte des images
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/image/logofr.png';
    if (imagePath.startsWith('http') || imagePath.startsWith('blob:')) return imagePath;
    // Détecter l'environnement basé sur le hostname au lieu de NODE_ENV
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    const baseUrl = isProduction ? '' : 'http://localhost:3002';
    return `${baseUrl}${imagePath.startsWith('/') ? imagePath : '/' + imagePath}`;
  };

  // Récupérer les événements depuis l'API
  useEffect(() => {
    fetchEvents();
    
    // Mettre à jour le statut des événements toutes les minutes
    const statusUpdateInterval = setInterval(() => {
      updateEventsStatus();
    }, 60000); // 60000 ms = 1 minute
    
    return () => clearInterval(statusUpdateInterval);
  }, []);

  useEffect(() => {
    // Filtrer les événements en fonction de la date actuelle
    if (events.length > 0) {
      // Mettre à jour le statut des événements avant de les filtrer
      updateEventsStatus();
      
      const upcoming = events.filter(event => event.status === 'upcoming');
      const past = events.filter(event => event.status === 'past');
      
      setUpcomingEvents(upcoming);
      setPastEvents(past);
    }
  }, [events, timeFilter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await api.getEvents();
      // Traitement de la réponse API
      
      // Vérifier la structure de la réponse et extraire les événements
      let eventsData = [];
      if (response.data && Array.isArray(response.data)) {
        eventsData = response.data;
      } else if (response.data && response.data.events && Array.isArray(response.data.events)) {
        eventsData = response.data.events;
      } else if (response.data && response.data.success && response.data.events && Array.isArray(response.data.events)) {
        eventsData = response.data.events;
      }
      

      
      // Si aucun événement n'est trouvé, utiliser les événements par défaut
      if (eventsData.length === 0) {
        // Aucun événement trouvé
        setEvents([]);
      } else {
        // Assurer que chaque événement a un ID et des chemins d'image complets
        const processedEvents = eventsData.map((event, index) => {
          // Construire le chemin complet pour l'image principale
          let fullImagePath = getImageUrl(event.image);
          
          // Construire les chemins complets pour la galerie
          let galleryWithFullPaths = [];
          if (event.gallery && Array.isArray(event.gallery)) {
            galleryWithFullPaths = event.gallery.map(img => getImageUrl(img));
          }
          
          return {
            ...event,
            id: event._id || event.id || `event-${index}`,
            status: event.status || (isEventPast(event.date, event.time) ? 'past' : 'upcoming'),
            image: fullImagePath,
            gallery: galleryWithFullPaths
          };
        });
        

        setEvents(processedEvents);
      }
    } catch (error) {
      // Gestion des erreurs
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Événements par défaut minimaux (utilisés uniquement en cas d'erreur de l'API)
  const defaultEvents = [];

  // Vérifier si un événement est passé en prenant en compte la date ET l'heure
  const isEventPast = (dateString, timeString) => {
    if (!dateString) return false;
    
    const eventDate = new Date(dateString);
    
    // Si l'heure est spécifiée, l'ajouter à la date de l'événement
    if (timeString) {
      const [hours, minutes] = timeString.split(':').map(Number);
      eventDate.setHours(hours, minutes, 0, 0);
    } else {
      // Si pas d'heure spécifiée, utiliser 23:59 comme heure par défaut (fin de journée)
      eventDate.setHours(23, 59, 0, 0);
    }
    
    return eventDate < new Date();
  };
  
  // Mettre à jour le statut des événements en fonction de la date et de l'heure actuelles
  const updateEventsStatus = () => {
    if (events.length === 0) return;
    
    const currentTime = new Date();

    
    // Créer une copie des événements pour les mettre à jour
    const updatedEvents = events.map(event => {
      // Déterminer si l'événement est passé en fonction de la date et de l'heure actuelles
      const shouldBePast = isEventPast(event.date, event.time);
      
      // Si le statut doit changer, mettre à jour l'événement
      if (shouldBePast && event.status === 'upcoming') {

        return { ...event, status: 'past' };
      }
      
      return event;
    });
    
    // Mettre à jour l'état avec les événements mis à jour
    if (JSON.stringify(updatedEvents) !== JSON.stringify(events)) {

      setEvents(updatedEvents);
    }
  };

  // Filtrer les événements en fonction des catégories et de la date
  const getFilteredEvents = () => {
    // Si aucun événement n'est chargé, retourner un tableau vide
    if (events.length === 0) return [];
    
    // Si le filtre est "all", afficher tous les événements (passés et à venir)
    // Sinon, filtrer par statut (upcoming/past)
    let filteredByStatus = activeFilter === 'all' 
      ? events 
      : (timeFilter === 'upcoming' 
          ? events.filter(event => event.status === 'upcoming')
          : events.filter(event => event.status === 'past'));
    
    // Filtrer ensuite par catégorie ou tag
    let result = filteredByStatus;
    
    if (activeFilter !== 'all') {
      // Vérifier si l'événement a le tag correspondant (true/false)
      if (activeFilter === 'health') {
        result = filteredByStatus.filter(event => event.health === true);
      } else if (activeFilter === 'culture') {
        result = filteredByStatus.filter(event => event.culture === true);
      } else if (activeFilter === 'sports') {
        result = filteredByStatus.filter(event => event.sports === true);
      } else if (activeFilter === 'professional') {
        result = filteredByStatus.filter(event => event.professional === true);
      } else if (activeFilter === 'internal') {
        result = filteredByStatus.filter(event => event.internal === true);
      } else if (activeFilter === 'external') {
        result = filteredByStatus.filter(event => event.external === true);
      } 
      // Filtrer par catégorie (valeur de chaîne)
      else if (activeFilter === 'workshop' || activeFilter === 'seminar' || activeFilter === 'conference' || activeFilter === 'training') {
        result = filteredByStatus.filter(event => event.category === activeFilter);
      }
    }
    
    return result;
  };

  // Fonctions utilitaires
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });

  // Gestion des événements
  const openEventDetails = (event) => setSelectedEvent(event);
  const closeEventDetails = () => setSelectedEvent(null);

  // Gestion du formulaire
  const handleAddEvent = async (e) => {
    e.preventDefault();
    
    // Filtrer les images de galerie non vides
    const filteredGallery = galleryImages.filter(url => url.trim() !== '');
    
    const eventData = {
      ...newEvent,
      status: isEventPast(newEvent.date) ? 'past' : 'upcoming',
      gallery: newEvent.status === 'past' ? filteredGallery : [],
      outcomes: newEvent.status === 'past' ? newEvent.outcomes.filter(outcome => outcome.trim() !== '') : []
    };
    
    try {
      await api.createEvent(eventData);
      // Rafraîchir la liste des événements
      fetchEvents();
      setShowAddEventForm(false);
      setNewEvent({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        category: 'workshop',
        image: '',
        gallery: [],
        outcomes: ['', '', ''],
        health: false,
        culture: false,
        sports: false,
        professional: false,
        internal: false,
        external: false,
        status: 'upcoming',
        type: 'internal'
      });
      setGalleryImages(['']);
    } catch (error) {
      console.error('Error adding event:', error);
    }
  };
  
  // Gestion des images de la galerie
  const handleAddGalleryImage = () => {
    setGalleryImages([...galleryImages, '']);
  };
  
  const handleRemoveGalleryImage = (index) => {
    const updatedGalleryImages = [...galleryImages];
    updatedGalleryImages.splice(index, 1);
    setGalleryImages(updatedGalleryImages);
  };
  
  const handleGalleryImageChange = (index, value) => {
    const updatedGalleryImages = [...galleryImages];
    updatedGalleryImages[index] = value;
    setGalleryImages(updatedGalleryImages);
  };
  
  // Gestion des résultats
  const handleOutcomeChange = (index, value) => {
    const updatedOutcomes = [...newEvent.outcomes];
    updatedOutcomes[index] = value;
    setNewEvent({ ...newEvent, outcomes: updatedOutcomes });
  };
  
  const handleAddOutcome = () => {
    setNewEvent({ ...newEvent, outcomes: [...newEvent.outcomes, ''] });
  };
  
  const handleRemoveOutcome = (index) => {
    const updatedOutcomes = [...newEvent.outcomes];
    updatedOutcomes.splice(index, 1);
    setNewEvent({ ...newEvent, outcomes: updatedOutcomes });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEvent({ ...newEvent, [name]: value });
  };

  const handleGalleryChange = (index, value) => {
    const updatedGallery = [...newEvent.gallery];
    updatedGallery[index] = value;
    setNewEvent({ ...newEvent, gallery: updatedGallery });
  };

  // Animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100, damping: 15 } }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 300, damping: 25 } },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } }
  };

  return (
    <div className={`events-page ${isRTL ? 'rtl' : ''}`}>
      <SEO 
        title={isArabic ? 'الفعاليات والأنشطة - المنظومة الوطنية للفرصة الجديدة' : 'Événements et activités - Dispositif National Nouvelle Chance'}
        description={isArabic ? 'اكتشف جميع الفعاليات والأنشطة المنظمة من قبل المنظومة الوطنية للفرصة الجديدة للشباب في تونس' : 'Découvrez tous les événements et activités organisés par le Dispositif National Nouvelle Chance pour les jeunes en Tunisie'}
        url="/events"
        type="website"
      />
      
      {/* Section Hero avec vidéos */}
      <section className="hero-section">
        <div className="hero-content">
          <h1>{t('events.title')}</h1>
          <p>{t('events.subtitle')}</p>
        </div>
        <div className="videos-container">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="video-item"
          >
            <source src={videoEvent1.toURL()} type="video/mp4" />
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
          <video
            autoPlay
            muted
            loop
            playsInline
            className="video-item"
          >
            <source src={videoEvent2.toURL()} type="video/mp4" />
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
          <video
            autoPlay
            muted
            loop
            playsInline
            className="video-item"
          >
            <source src={videoEvent3.toURL()} type="video/mp4" />
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
          <video
            autoPlay
            muted
            loop
            playsInline
            className="video-item"
          >
            <source src={videoEvent4.toURL()} type="video/mp4" />
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
        </div>
        <div className="overlay"></div>
      </section>

      {/* Section des événements */}
      <section className="bg-light">
        <div className="events-layout">
            {/* Sidebar des filtres */}
            <aside className="filters-sidebar">
              <h2 className="filters-title">{t('events.filters.title')}</h2>
              
              {/* Filtre Tous par défaut */}
              <div className="filter-section">
                <h3 className="filter-section-title">{isArabic ? 'جميع الأحداث' : 'Tous les événements'}</h3>
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`filter-button-sidebar ${activeFilter === 'all' ? 'active' : ''}`}
                >
                  {t('events.filters.all')}
                </button>
              </div>

              {/* Filtres temporels - Masqué quand "Tous" est sélectionné */}
              {activeFilter !== 'all' && (
                <div className="filter-section">
                  <h3 className="filter-section-title">{isArabic ? 'التوقيت' : 'Période'}</h3>
                  <button
                    className={`filter-button-sidebar ${timeFilter === 'upcoming' ? 'active' : ''}`}
                    onClick={() => setTimeFilter('upcoming')}
                  >
                    {t('events.upcoming')}
                  </button>
                  <button
                    className={`filter-button-sidebar ${timeFilter === 'past' ? 'active' : ''}`}
                    onClick={() => setTimeFilter('past')}
                  >
                    {t('events.past')}
                  </button>
                </div>
              )}

              {/* Type d'événement */}
              <div className="filter-section">
                <h3 className="filter-section-title">{isArabic ? 'نوع الحدث' : 'Type d\'événement'}</h3>
                <button
                  onClick={() => setActiveFilter('workshop')}
                  className={`filter-button-sidebar ${activeFilter === 'workshop' ? 'active' : ''}`}
                >
                  {t('events.filters.workshop')}
                </button>
                <button
                  onClick={() => setActiveFilter('seminar')}
                  className={`filter-button-sidebar ${activeFilter === 'seminar' ? 'active' : ''}`}
                >
                  {t('events.filters.seminar')}
                </button>
                <button
                  onClick={() => setActiveFilter('conference')}
                  className={`filter-button-sidebar ${activeFilter === 'conference' ? 'active' : ''}`}
                >
                  {t('events.filters.conference')}
                </button>
                <button
                  onClick={() => setActiveFilter('training')}
                  className={`filter-button-sidebar ${activeFilter === 'training' ? 'active' : ''}`}
                >
                  {t('events.filters.training')}
                </button>
              </div>

              {/* Catégories */}
              <div className="filter-section">
                <h3 className="filter-section-title">{isArabic ? 'الفئات' : 'Catégories'}</h3>
                <button
                  onClick={() => setActiveFilter('health')}
                  className={`filter-button-sidebar ${activeFilter === 'health' ? 'active' : ''}`}
                >
                  {t('events.filters.health')}
                </button>
                <button
                  onClick={() => setActiveFilter('culture')}
                  className={`filter-button-sidebar ${activeFilter === 'culture' ? 'active' : ''}`}
                >
                  {t('events.filters.culture')}
                </button>
                <button
                  onClick={() => setActiveFilter('sports')}
                  className={`filter-button-sidebar ${activeFilter === 'sports' ? 'active' : ''}`}
                >
                  {t('events.filters.sports')}
                </button>
                <button
                  onClick={() => setActiveFilter('professional')}
                  className={`filter-button-sidebar ${activeFilter === 'professional' ? 'active' : ''}`}
                >
                  {t('events.filters.professional')}
                </button>
                <button
                  onClick={() => setActiveFilter('internal')}
                  className={`filter-button-sidebar ${activeFilter === 'internal' ? 'active' : ''}`}
                >
                  {t('events.filters.internal')}
                </button>
                <button
                  onClick={() => setActiveFilter('external')}
                  className={`filter-button-sidebar ${activeFilter === 'external' ? 'active' : ''}`}
                >
                  {t('events.filters.external')}
                </button>
              </div>
            </aside>
            {/* Contenu principal des événements */}
            <div className="events-main-content">
              {/* Affichage des événements filtrés */}
              {getFilteredEvents().length > 0 ? (
                <>
                  {/* Événements à venir (si le filtre le permet) */}
                  {getFilteredEvents().some(event => event.status === 'upcoming') && (
                    <div className="mb-16">
                      <h2 className="text-2xl font-bold mb-8 text-center text-primary">{t('events.upcomingEvents')}</h2>
                      <motion.div 
                        variants={containerVariants} 
                        initial="hidden" 
                        animate="visible" 
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                      >
                        {getFilteredEvents()
                          .filter(event => event.status === 'upcoming')
                          .map(event => (
                            <motion.div key={event.id} variants={itemVariants} className="modern-event-card"
                              whileHover={{ y: -10 }}
                              onClick={() => navigate(`/events/${event._id || event.id}`)}
                            >
                              <div className="modern-event-image-wrapper">
                                <img 
                                  src={getImageUrl(event.image)} 
                                  alt={event.title} 
                                  className="modern-event-image" 
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/image/logofr.png';
                                  }}
                                />
                                <div className="modern-event-overlay">
                                  <span className="modern-event-category">
                                    {t(`events.filters.${event.category}`)}
                                  </span>
                                </div>
                              </div>
                              <div className="modern-event-content">
                                <div className="modern-event-header">
                                  <h3 className="modern-event-title">{isArabic && event.titleAr ? event.titleAr : event.title}</h3>
                                  
                                  <div className="modern-event-tags">
                                    {event.health && <span className="tag-badge health">{t('events.tags.health')}</span>}
                                    {event.culture && <span className="tag-badge culture">{t('events.tags.culture')}</span>}
                                    {event.sports && <span className="tag-badge sports">{t('events.tags.sports')}</span>}
                                  </div>
                                </div>
                                <div className="modern-event-meta">
                                  <div className="meta-item">
                                    <FaCalendarAlt />
                                    <span>{formatDate(event.date)}</span>
                                  </div>
                                  <div className="meta-item">
                                    <FaClock />
                                    <span>{event.time}</span>
                                  </div>
                                </div>
                                <div className="modern-event-footer">
                                  
                                  <button className="view-details-btn">
                                    {t('events.viewDetails')}
                                    <FaArrowRight className={isRTL ? 'rotate-180' : ''} />
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                      </motion.div>
                    </div>
                  )}

                  {/* Événements passés (si le filtre le permet) */}
                  {getFilteredEvents().some(event => event.status === 'past') && (
                    <div>
                      <h2 className="text-2xl font-bold mb-8 text-center text-primary">{t('events.pastEvents')}</h2>
                      <motion.div 
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                      >
                        {getFilteredEvents()
                          .filter(event => event.status === 'past')
                          .map(event => (
                            <motion.div 
                              key={event.id}
                              variants={itemVariants}
                              className="modern-event-card past-event"
                              whileHover={{ y: -10 }}
                              onClick={() => navigate(`/events/${event._id || event.id}`)}
                            >
                              <div className="modern-event-image-wrapper">
                                <img 
                                  src={getImageUrl(event.image)} 
                                  alt={event.title} 
                                  className="modern-event-image" 
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/image/logofr.png';
                                  }}
                                />
                                <div className="modern-event-overlay past">
                                  <span className="modern-event-category">
                                    {t(`events.filters.${event.category}`)}
                                  </span>
                                  <span className="past-badge">{t('events.pastEvent')}</span>
                                </div>
                              </div>
                              <div className="modern-event-content">
                                <div className="modern-event-header">
                                  <h3 className="modern-event-title">{isArabic && event.titleAr ? event.titleAr : event.title}</h3>
                                  <div className="modern-event-tags">
                                    {event.health && <span className="tag-badge health">{t('events.tags.health')}</span>}
                                    {event.culture && <span className="tag-badge culture">{t('events.tags.culture')}</span>}
                                    {event.sports && <span className="tag-badge sports">{t('events.tags.sports')}</span>}
                                  </div>
                                </div>
                                <div className="modern-event-meta">
                                  <div className="meta-item">
                                    <FaCalendarAlt />
                                    <span>{formatDate(event.date)}</span>
                                  </div>
                                  <div className="meta-item">
                                    <FaClock />
                                    <span>{event.time}</span>
                                  </div>
                                </div>
                                <div className="modern-event-footer">
                                  <button className="view-details-btn">
                                    {t('events.viewDetails')}
                                    <FaArrowRight className={isRTL ? 'rotate-180' : ''} />
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                      </motion.div>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-events-container">
                  <img src="/image/logofr.png" alt="DNC Logo" className="no-events-logo" />
                  <h3 className="no-events-title">{t('events.noEvents')}</h3>
                  <button 
                    onClick={() => navigate('/')}
                    className="no-events-button"
                  >
                    {isArabic ? 'العودة إلى الصفحة الرئيسية' : 'Retour à l\'accueil'}
                  </button>
                </div>
              )}
            </div>
          </div>
      </section>

      {/* Modal pour les détails de l'événement */}
      <AnimatePresence>
        {selectedEvent && (
          <div className="event-modal-overlay">
            <motion.div 
              className="event-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <button 
                onClick={closeEventDetails}
                className="event-modal-close"
                aria-label={t('events.closeDetails')}
              >
                <FaTimes />
              </button>
              
              <img 
                src={getImageUrl(selectedEvent.image)} 
                alt={selectedEvent.title} 
                className="event-modal-image" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/image/logofr.png';
                }}
              />
              
              <div className="event-modal-body">
                <h2 className="event-modal-title">{isArabic && selectedEvent.titleAr ? selectedEvent.titleAr : selectedEvent.title}</h2>  
                  <div className="event-modal-info-item">
                    <FaMapMarkerAlt className="event-modal-info-icon" />
                    <div className="event-modal-info-content">
                      <p className="event-modal-info-label">{t('events.location')}</p>
                      <p className="event-modal-info-value">{isArabic && selectedEvent.locationAr ? selectedEvent.locationAr : selectedEvent.location}</p>
                    </div>
                  </div>
                  <div className="event-modal-info">
                  <div className="event-modal-info-item">
                    <FaCalendarAlt className="event-modal-info-icon" />
                    <div className="event-modal-info-content">
                      <p className="event-modal-info-label">{t('events.date')}</p>
                      <p className="event-modal-info-value">{formatDate(selectedEvent.date)}</p>
                    </div>
                  </div>
                  <div className="event-modal-info-item">
                    <FaClock className="event-modal-info-icon" />
                    <div className="event-modal-info-content">
                      <p className="event-modal-info-label">{t('events.time')}</p>
                      <p className="event-modal-info-value">{selectedEvent.time}</p>
                    </div>
                  </div>
                  
                </div>
                
                {/* Galerie photo (uniquement pour les événements passés) */}
                {selectedEvent.status === 'past' && selectedEvent.gallery && selectedEvent.gallery.length > 0 && (
                  <div className="event-modal-section">
                    <h3 className="event-modal-section-title">{t('events.gallery')}</h3>
                    <div className="event-modal-gallery">
                      {selectedEvent.gallery.map((image, index) => {
                        // Construire l'URL correcte pour l'image
                        const imageUrl = getImageUrl(image);
                        
                        return (
                          <img 
                            key={index} 
                            src={imageUrl} 
                            alt={`${selectedEvent.title} - ${index + 1}`} 
                            className="event-modal-gallery-image" 
                            onClick={() => window.open(imageUrl, '_blank')}
                            onError={(e) => {

                              e.target.onerror = null;
                              e.target.src = '/image/logofr.png';
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Résultats et impact (uniquement pour les événements passés) */}
                {selectedEvent.status === 'past' && selectedEvent.outcomes && selectedEvent.outcomes.length > 0 && (
                  <div className="event-modal-section">
                    <h3 className="event-modal-section-title">{t('events.outcomes')}</h3>
                    <ul className="event-modal-outcomes">
                      {(isArabic && selectedEvent.outcomesAr && selectedEvent.outcomesAr.length > 0 ? selectedEvent.outcomesAr : selectedEvent.outcomes).map((outcome, index) => (
                        <li key={index} className="event-modal-outcome-item">{outcome}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="event-modal-footer">
                  <button 
                    onClick={closeEventDetails}
                    className="event-modal-button"
                  >
                    {t('events.closeDetails')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Events;
